/**
 * Binder image-attachment orchestration for the web app. Uploads/downloads
 * ciphertext directly to S3 through presigned URLs (the Lambda never sees the
 * bytes). Each stored S3 object is `iv(12) || AES-GCM-ciphertext`; the content
 * key is ECIES-wrapped to the family public key and stored in the AttachmentRef
 * manifest, which rides inside the item's own ciphertext. See spec §3.3.
 */
import type { AttachmentRef } from '@heybesafe/shared';
import { wrapAttachmentKey, unwrapAttachmentKey } from '@heybesafe/shared';
import {
  generateContentKey, exportContentKey, importContentKey, encryptBytes, decryptBytes,
} from '../crypto/attachmentCrypto';
import { makeThumbnail } from './imageThumbnail';
import { createAttachmentUpload, getAttachmentDownload, deleteAttachment } from '../api/client';

export interface UploadContext {
  orgId: string;
  itemId: string;
  familyPublicKey: Uint8Array;
}
export interface DownloadContext {
  orgId: string;
  itemId: string;
  familyPrivateKey: Uint8Array;
  familyPublicKey: Uint8Array;
}

function randomAttachmentId(): string {
  return crypto.randomUUID().replace(/-/g, '');
}

/** iv(12) || ciphertext */
function frame(iv: Uint8Array, ciphertext: Uint8Array): Uint8Array {
  const out = new Uint8Array(iv.length + ciphertext.length);
  out.set(iv, 0);
  out.set(ciphertext, iv.length);
  return out;
}

async function putCiphertext(url: string, mime: string, body: Uint8Array): Promise<Response> {
  return fetch(url, { method: 'PUT', headers: { 'Content-Type': mime }, body: body as BodyInit });
}

/** Real browsers implement Blob.arrayBuffer(); jsdom (our test environment)
 * does not, so fall back to FileReader there. */
function readBlobBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer().then((buf) => new Uint8Array(buf));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsArrayBuffer(blob);
  });
}

/** Encrypt bytes with `key`, then presign + PUT. Re-presigns once on failure
 * (presigned PUTs expire after 300s). */
async function encryptAndUpload(
  orgId: string,
  itemId: string,
  attachmentId: string,
  mime: string,
  plain: Uint8Array,
  key: CryptoKey,
): Promise<void> {
  const { ciphertext, iv } = await encryptBytes(plain, key);
  const framed = frame(iv, ciphertext);
  for (let attempt = 0; attempt < 2; attempt++) {
    const presign = await createAttachmentUpload({ orgId, itemId, attachmentId, mime, size: framed.length });
    const res = await putCiphertext(presign.uploadUrl, mime, framed);
    if (res.ok) return;
    if (attempt === 1) throw new Error(`attachment upload failed: ${res.status}`);
  }
}

export async function uploadAttachment(file: File, ctx: UploadContext): Promise<AttachmentRef> {
  const key = await generateContentKey();
  const attachmentId = randomAttachmentId();

  const fullBytes = await readBlobBytes(file);
  await encryptAndUpload(ctx.orgId, ctx.itemId, attachmentId, file.type, fullBytes, key);

  const thumbBlob = await makeThumbnail(file);
  const thumbBytes = await readBlobBytes(thumbBlob);
  const thumbId = `${attachmentId}.thumb`;
  await encryptAndUpload(ctx.orgId, ctx.itemId, thumbId, thumbBlob.type || 'image/webp', thumbBytes, key);

  const rawKey = await exportContentKey(key);
  const wrapped = await wrapAttachmentKey(rawKey, ctx.familyPublicKey);

  return {
    attachmentId,
    s3Key: `org/${ctx.orgId}/${ctx.itemId}/${attachmentId}`,
    wrappedContentKey: wrapped.wrappedContentKey,
    wrappedContentKeyIv: wrapped.wrappedContentKeyIv,
    ephemeralPublicKey: wrapped.ephemeralPublicKey,
    filename: file.name,
    mime: file.type,
    size: fullBytes.length,
    thumbnailKey: `org/${ctx.orgId}/${ctx.itemId}/${thumbId}`,
  };
}

export async function fetchAttachmentBytes(
  ref: AttachmentRef,
  ctx: DownloadContext,
  opts?: { thumb?: boolean },
): Promise<{ bytes: Uint8Array; mime: string }> {
  const id = opts?.thumb ? `${ref.attachmentId}.thumb` : ref.attachmentId;
  let res: Response | undefined;
  for (let attempt = 0; attempt < 2; attempt++) {
    const presign = await getAttachmentDownload(id, { orgId: ctx.orgId, itemId: ctx.itemId });
    res = await fetch(presign.downloadUrl);
    if (res.ok) break;
    if (res.status !== 403 || attempt === 1) throw new Error(`attachment download failed: ${res.status}`);
  }
  const framed = new Uint8Array(await res!.arrayBuffer());
  const iv = framed.slice(0, 12);
  const ciphertext = framed.slice(12);

  const rawKey = await unwrapAttachmentKey(
    {
      wrappedContentKey: ref.wrappedContentKey,
      wrappedContentKeyIv: ref.wrappedContentKeyIv,
      ephemeralPublicKey: ref.ephemeralPublicKey,
    },
    ctx.familyPrivateKey,
    ctx.familyPublicKey,
  );
  const key = await importContentKey(rawKey);
  const bytes = await decryptBytes(ciphertext, iv, key);
  // Thumbnails are always image/webp; full-res carries the original mime.
  return { bytes, mime: opts?.thumb ? 'image/webp' : ref.mime };
}

export async function downloadAttachment(
  ref: AttachmentRef,
  ctx: DownloadContext,
  opts?: { thumb?: boolean },
): Promise<string> {
  const { bytes, mime } = await fetchAttachmentBytes(ref, ctx, opts);
  return URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime }));
}

/** Best-effort per-attachment cleanup. The server deletes both the full-res and
 * `.thumb` objects for the id, so one call suffices. A failure is swallowed —
 * the whole-document sweep on delete is the backstop (spec §6). */
export async function deleteAttachmentRef(
  ref: AttachmentRef,
  ctx: { orgId: string; itemId: string },
): Promise<void> {
  try {
    await deleteAttachment(ref.attachmentId, { orgId: ctx.orgId, itemId: ctx.itemId });
  } catch {
    // swallow — see doc comment
  }
}
